<template>
  <v-dialog
    v-model="dialogModel"
    max-width="720"
  >
    <v-card
      v-if="request"
      prepend-icon="mdi-pencil"
      :title="`Editar Requisição: ${request.code}`"
    >
      <v-card-text>
        <v-form ref="form" @submit.prevent="saveRequest">
          <v-row dense>
            <v-col
              cols="12"
              md="12"
              sm="12"
            >
              <v-text-field
                v-model="form.description"
                label="Descrição"
                required
                :rules="[v => !!v || 'Descrição é obrigatória']"
              ></v-text-field>
            </v-col>

            <v-col
              cols="12"
              md="6"
              sm="6"
            >
              <v-select
                v-model="form.sectorId"
                label="Setor"
                :items="sectors"
                item-title="description"
                item-value="id"
                required
                :rules="[v => !!v || 'Setor é obrigatório']"
              ></v-select>
            </v-col>
            
            <v-col
              cols="12"
              md="6"
              sm="6"
            >
            <v-radio-group
                  v-model="form.priority"
                  label="Prioridade"
                  required
                  inline
                  :rules="[v => !!v || 'Prioridade é obrigatória']"
                  row
                >
                  <v-radio
                    label="NORMAL"
                    value="LOW"
                    color="success"
                  ></v-radio>
                  <v-radio
                    label="ALTA"
                    value="MEDIUM"
                    color="warning"
                  ></v-radio>
                  <v-radio
                    label="URGENTE"
                    value="HIGH"
                    color="error"
                  ></v-radio>
                </v-radio-group>
            </v-col>
            
            <v-col
              cols="12"
            >
              <v-textarea
                v-model="form.note"
                label="Observações"
                rows="3"
              ></v-textarea>
            </v-col>
          </v-row>
        </v-form>
        
        <v-divider class="my-4"></v-divider>
        
        <div v-if="request.status !== REQUEST_STATUS.PENDING.id">
          <h3 class="text-h6 mb-2">Opções de Compra</h3>
        
          <div v-if="requestOptions.length > 0">
            <v-expansion-panels>
              <v-expansion-panel
                v-for="option in requestOptions"
                :key="option.id"
              >
                <v-expansion-panel-title>
                  {{ option.supplier }} - R$ {{ option.price.toFixed(2) }} ({{ option.unitType }})
                  <template v-slot:actions>
                    <v-btn
                      icon="mdi-pencil"
                      variant="text"
                      size="small"
                      color="primary"
                      @click.stop="editOption(option)"
                    ></v-btn>
                    <v-btn
                      icon="mdi-delete"
                      variant="text"
                      size="small"
                      color="error"
                      @click.stop="confirmDeleteOption(option)"
                    ></v-btn>
                  </template>
                </v-expansion-panel-title>
                <v-expansion-panel-text>
                  <v-row dense>
                    <v-col cols="12" md="6">
                      <v-list-item>
                        <v-list-item-title>Fornecedor</v-list-item-title>
                        <v-list-item-subtitle>{{ option.supplier }}</v-list-item-subtitle>
                      </v-list-item>
                    </v-col>
                    
                    <v-col cols="12" md="6">
                      <v-list-item>
                        <v-list-item-title>Preço Unitário</v-list-item-title>
                        <v-list-item-subtitle>R$ {{ option.price.toFixed(2) }}</v-list-item-subtitle>
                      </v-list-item>
                    </v-col>
                    
                    <v-col cols="12" md="6">
                      <v-list-item>
                        <v-list-item-title>Quantidade</v-list-item-title>
                        <v-list-item-subtitle>{{ option.quantity }}</v-list-item-subtitle>
                      </v-list-item>
                    </v-col>
                    
                    <v-col cols="12" md="6">
                      <v-list-item>
                        <v-list-item-title>Unidade</v-list-item-title>
                        <v-list-item-subtitle>{{ option.unitType }}</v-list-item-subtitle>
                      </v-list-item>
                    </v-col>
                    
                    <v-col cols="6" md="6">
                      <v-list-item>
                        <v-list-item-title>Condição de Pagamento</v-list-item-title>
                        <v-list-item-subtitle>{{ option.paymentTerms }}</v-list-item-subtitle>
                      </v-list-item>
                    </v-col>

                    <v-col cols="6" md="6">
                      <v-list-item>
                        <v-list-item-title>Previsão de Entrega</v-list-item-title>
                        <v-list-item-subtitle>{{ formattedEta(option.eta) }}</v-list-item-subtitle>
                      </v-list-item>
                    </v-col>
                    
                    <v-col cols="12">
                      <v-list-item v-if="option.note">
                        <v-list-item-title>Observações</v-list-item-title>
                        <v-list-item-subtitle>{{ option.note }}</v-list-item-subtitle>
                      </v-list-item>
                    </v-col>
                    
                    <v-col cols="12">
                      <v-list-item>
                        <v-list-item-title>Total</v-list-item-title>
                        <v-list-item-subtitle class="text-subtitle-1 font-weight-bold">
                          {{ formattedTotal(option) }}
                        </v-list-item-subtitle>
                      </v-list-item>
                    </v-col>
                  </v-row>
                </v-expansion-panel-text>
              </v-expansion-panel>
            </v-expansion-panels>
          </div>
        
          <v-alert
            v-else
            type="info"
            variant="tonal"
            class="mt-2"
          >
            Nenhuma opção de compra cadastrada.
          </v-alert>
        
          <div class="d-flex justify-end mt-2">
            <v-btn
              color="primary"
              prepend-icon="mdi-plus"
              variant="tonal"
              @click="addOption"
            >
              Adicionar Opção
            </v-btn>
          </div>
        </div>

        <v-expansion-panels variant="accordion" class="mt-4">
          <v-expansion-panel>
            <v-expansion-panel-title>
              <h3 class="text-h6">Histórico</h3>
            </v-expansion-panel-title>
            
            <v-expansion-panel-text>
              <v-timeline density="compact" align="start">
                <v-timeline-item
                  v-for="(log, index) in activityLogs"
                  :key="index"
                  dot-color="primary"
                  size="small"
                >
                  <div>
                    <div class="text-subtitle-2">{{ formatDate(log.createdAt) }}</div>
                    <div class="d-flex align-center">
                      <v-icon size="small" class="me-1">mdi-account</v-icon>
                      <span class="text-caption font-weight-bold">{{ getUserName(log.userId) }}</span>
                    </div>
                    <div class="text-body-2 mt-1">{{ log.description }}</div>
                  </div>
                </v-timeline-item>
              </v-timeline>
            </v-expansion-panel-text>
          </v-expansion-panel>
        </v-expansion-panels>
      </v-card-text>
      
      <v-divider></v-divider>
      
      <v-card-actions>
        <v-spacer></v-spacer>
        
        <v-btn
          text="Cancelar"
          variant="plain"
          @click="close"
        ></v-btn>
        
        <v-btn
          color="primary"
          text="Salvar"
          variant="flat"
          :disabled="!isFormValid"
          @click="saveRequest"
        ></v-btn>
      </v-card-actions>
    </v-card>
    
    <OptionDialog
      v-model="optionDialog"
      :option="selectedOption"
      :request-id="request?.id"
      @close="optionDialog = false"
      @save="handleOptionSave"
    />
    
    <v-dialog
      v-model="confirmDialog"
      max-width="400"
    >
      <v-card
        title="Confirmar Exclusão"
        prepend-icon="mdi-alert"
      >
        <v-card-text>
          Tem certeza que deseja excluir esta opção de compra?
          Esta ação não pode ser desfeita.
        </v-card-text>
        
        <v-card-actions>
          <v-spacer></v-spacer>
          
          <v-btn
            text="Cancelar"
            variant="plain"
            @click="confirmDialog = false"
          ></v-btn>
          
          <v-btn
            color="error"
            text="Excluir"
            variant="flat"
            @click="deleteOption"
          ></v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-dialog>
</template>

<script>
import OptionDialog from './OptionDialog.vue';

import { REQUEST_STATUS } from '@/helpers/constants/app.constants';
import { mapGetters, mapActions, mapState } from 'vuex';

export default {
  name: 'EditRequestDialog',
  
  components: {
    OptionDialog
  },
  
  props: {
    modelValue: {
      type: Boolean,
      default: false
    },
    request: {
      type: Object,
      default: null
    }
  },
  
  data: () => ({
    REQUEST_STATUS,
    form: {
      id: null,
      code: '',
      description: '',
      sectorId: '',
      requestDate: '',
      note: '',
      userId: '',
      status: '',
    },
    optionDialog: false,
    confirmDialog: false,
    selectedOption: null
  }),
  
  computed: {
    ...mapGetters('general', ['sectors', 'suppliers', 'paymentMethods', 'unitTypes']),
    ...mapState('activityLogs', ['activityLogs']),
    ...mapState('user', ['allUsers']),

    dialogModel: {
      get() {
        return this.modelValue;
      },
      set(value) {
        this.$emit('update:modelValue', value);
      }
    },
    
    requestOptions() {
      return this.request.requestOptions.map((option) => {
        return {
          ...option,
          supplier: this.suppliers.find((supplier) => supplier.id === option.supplierId)?.description,
          paymentTerms: this.paymentMethods.find((paymentMethod) => paymentMethod.id === option.paymentMethodId)?.description,
          unitType: this.unitTypes.find((unitType) => unitType.id === option.unitId)?.description,
        }
      });
    },
    
    isFormValid() {
      return this.form.description && this.form.sectorId && this.form.requestDate;
    }
  },
  
  watch: {
    request: {
      handler(newRequest) {
        if (newRequest) {
          this.form = { ...newRequest };
          this.fetchActivityLogs(newRequest.id);
        }
      },
      immediate: true
    }
  },
  
  methods: {
    ...mapActions('request', ['createOption', 'updateOption', 'deleteOption']),
    ...mapActions('activityLogs', ['fetchActivityLogs']),

    formattedEta(dateString) {
      const date = new Date(dateString);
      return date.toLocaleDateString('pt-BR');
    },

    formatDate(dateString) {
      const date = new Date(dateString);
      return date.toLocaleDateString('pt-BR');
    },

    getUserName(userId) {
      const user = this.allUsers.find((user) => user.id === userId);
      return user ? `${user.firstName} ${user.lastName}` : 'Usuário não encontrado';
    },

    addOption() {
      this.selectedOption = null;
      this.optionDialog = true;
    },
    
    editOption(option) {
      this.selectedOption = { ...option };
      this.optionDialog = true;
    },
    
    confirmDeleteOption(option) {
      this.selectedOption = option;
      this.confirmDialog = true;
    },

    formattedTotal(option) {
      const total = (option.price || 0) * (option.quantity || 0);
      return new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL',
        minimumFractionDigits: 2
      }).format(total);
    },
    
    async deleteOption() {
      try {
        const { success, error } = await this.deleteOption(this.selectedOption.id);
        
        if (success) {
          this.$emit('snackbar', { color: 'success', text: 'Opção de compra excluída com sucesso!' });
        } else {
          this.$emit('snackbar', { color: 'error', text: `Erro ao excluir opção de compra: ${error}` });
        }
        
        this.confirmDialog = false;
      } catch (error) {
        this.$emit('snackbar', { color: 'error', text: `Erro: ${error.message}` });
      }
    },
    
    async handleOptionSave(optionData) {
      try {
        if (optionData.id) {
          const { success, error } = await this.updateOption(optionData);
          
          if (success) {
            this.$emit('snackbar', { color: 'success', text: 'Opção de compra atualizada com sucesso!' });
          } else {
            this.$emit('snackbar', { color: 'error', text: `Erro ao atualizar opção de compra: ${error}` });
          }
        } else {
          const { success, error } = await this.createOption(optionData);
          
          if (success) {
            this.$emit('snackbar', { color: 'success', text: 'Opção de compra adicionada com sucesso!' });
          } else {
            this.$emit('snackbar', { color: 'error', text: `Erro ao adicionar opção de compra: ${error}` });
          }
        }
        
        this.optionDialog = false;
      } catch (error) {
        this.$emit('snackbar', { color: 'error', text: `Erro: ${error.message}` });
      }
    },
    
    async saveRequest() {
      if (!this.isFormValid) return;
      
      try {
        this.$emit('save', this.form);
        this.close();
      } catch (error) {
        this.$emit('snackbar', { color: 'error', text: `Erro: ${error.message}` });
      }
    },
    
    close() {
      this.dialogModel = false;
      this.$emit('close');
    }
  }
}
</script> 